import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Download, DollarSign, CreditCard, AlertCircle, CheckCircle2, Edit2, Clock, Building2, CalendarIcon, Undo2, History, Search } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { isAdmin } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { ResilientFetchBanner, runWithSettled } from "@/lib/resilientFetch";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const PAGE_SIZE = 50;

const PaymentManagement = ({ user, onLogout }) => {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchFailures, setFetchFailures] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all, pending, completed
  const [typeFilter, setTypeFilter] = useState("all"); // all, enrollment, rent
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 350);

  // Pagination — backend serves pages of 50 across enrollment + rent payments.
  const [page, setPage] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Lazy-loaded dialog lookups (students / enrollments / batches) — only
  // fetched when the "Record Payment" dialog first opens.
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  const [recordingPayment, setRecordingPayment] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [refundingPayment, setRefundingPayment] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: "", reason: "" });
  const [auditPayment, setAuditPayment] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [recordingRentPayment, setRecordingRentPayment] = useState(null);
  const [rentPayForm, setRentPayForm] = useState({
    amount: "", payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'),
    reference_number: "", notes: "",
  });
  
  const [paymentForm, setPaymentForm] = useState({
    student_id: "",
    enrollment_id: "",
    amount: "",
    payment_method: "cash",
    cheque_bank_name: "",
    cheque_number: "",
    notes: "",
  });

  const [recordForm, setRecordForm] = useState({
    payment_method: "cash",
    cheque_bank_name: "",
    cheque_number: "",
    record_amount: "",
  });

  const [editForm, setEditForm] = useState({
    amount: "",
    payment_method: "cash",
    cheque_bank_name: "",
    cheque_number: "",
    notes: "",
  });

  useEffect(() => {
    // Any filter change → reset to page 1 and refetch payments only.
    setPage(1);
    fetchData(1, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filter, typeFilter]);

  const buildPaymentsParams = (pageNum) => {
    const params = new URLSearchParams();
    params.append("page", String(pageNum));
    params.append("page_size", String(PAGE_SIZE));
    if (debouncedSearch) params.append("search", debouncedSearch);
    if (filter && filter !== "all") params.append("status", filter);
    if (typeFilter && typeFilter !== "all") params.append("payment_type", typeFilter);
    return params;
  };

  const fetchData = async (pageNum = 1, { append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    const params = buildPaymentsParams(pageNum);
    const FETCHERS = {
      "payments": () => api.get(`/payments?${params.toString()}`),
    };
    const { results, failures } = await runWithSettled(FETCHERS);
    if (results["payments"]) {
      const data = results["payments"].data;
      const incoming = data.payments || [];
      setPayments((prev) => (append ? [...prev, ...incoming] : incoming));
      setTotalPayments(data.total ?? incoming.length);
      setHasMore(Boolean(data.has_more));
    }
    setFetchFailures(failures);
    if (append) setLoadingMore(false);
    else setLoading(false);
  };

  const loadMorePayments = async () => {
    const next = page + 1;
    setPage(next);
    await fetchData(next, { append: true });
  };

  // Heavy lookups: only used inside the "Record Payment" dialog dropdowns.
  // Fetched once, on first dialog interaction, NOT on page mount.
  const fetchLookups = async () => {
    if (lookupsLoaded || lookupsLoading) return;
    setLookupsLoading(true);
    const FETCHERS = {
      "students":    () => api.get("/users?role=student&page=1&page_size=200"),
      "enrollments": () => api.get("/enrollments?include_batch_details=true"),
      "batches":     () => api.get("/batches"),
    };
    const { results, failures } = await runWithSettled(FETCHERS);
    if (results["students"])    setStudents(results["students"].data.users);
    if (results["enrollments"]) setEnrollments(results["enrollments"].data.enrollments);
    if (results["batches"])     setBatches(results["batches"].data.batches);
    if (!failures.length || Object.keys(results).length > 0) setLookupsLoaded(true);
    setLookupsLoading(false);
  };

  const retryFailed = async () => {
    if (!fetchFailures.length) return;
    setRetrying(true);
    await fetchData(page, { append: false });
    setRetrying(false);
    if (!fetchFailures.length) toast.success("Refreshed");
  };

  const recordRentPayment = async (paymentId, bookingId) => {
    const amt = parseFloat(rentPayForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/rent-payments/${bookingId}/record`, {
        amount: amt,
        payment_method: rentPayForm.payment_method,
        payment_date: rentPayForm.payment_date,
        reference_number: rentPayForm.reference_number || null,
        notes: rentPayForm.notes || null,
      });
      toast.success(`₹${amt} recorded successfully`);
      setRecordingRentPayment(null);
      setRentPayForm({ amount: "", payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'), reference_number: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const studentEnrollments = enrollments.filter(
    e => e.student_id === paymentForm.student_id && e.status === "active"
  );

  const recordNewPayment = async () => {
    if (!paymentForm.student_id || !paymentForm.enrollment_id || !paymentForm.amount) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await api.post("/payments", {
        student_id: paymentForm.student_id,
        enrollment_id: paymentForm.enrollment_id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_status: "completed",
        payment_date: new Date().toISOString(),
        cheque_bank_name: paymentForm.payment_method === "cheque" ? paymentForm.cheque_bank_name : null,
        cheque_number: paymentForm.payment_method === "cheque" ? paymentForm.cheque_number : null,
        notes: paymentForm.notes,
      });

      toast.success("Payment recorded successfully!");
      fetchData();
      setOpen(false);
      setPaymentForm({
        student_id: "",
        enrollment_id: "",
        amount: "",
        payment_method: "cash",
        cheque_bank_name: "",
        cheque_number: "",
        notes: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const recordPendingPayment = async (paymentId) => {
    const payment = payments.find(p => p.payment_id === paymentId);
    const balance = payment?.balance_amount || 0;
    const recordAmount = parseFloat(recordForm.record_amount) || balance;
    
    if (recordAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (recordAmount > balance) {
      toast.error(`Amount cannot exceed outstanding balance of ₹${balance}`);
      return;
    }
    
    try {
      await api.post(`/payments/${paymentId}/record-partial`, {
        amount: recordAmount,
        payment_method: recordForm.payment_method,
        cheque_bank_name: recordForm.payment_method === "cheque" ? recordForm.cheque_bank_name : null,
        cheque_number: recordForm.payment_method === "cheque" ? recordForm.cheque_number : null,
      });
      toast.success(`₹${recordAmount} recorded successfully`);
      setRecordingPayment(null);
      setRecordForm({ payment_method: "cash", cheque_bank_name: "", cheque_number: "", record_amount: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const startEditPayment = (payment) => {
    setEditingPayment(payment.payment_id);
    setEditForm({
      amount: payment.amount.toString(),
      payment_method: payment.payment_method || "cash",
      cheque_bank_name: payment.cheque_bank_name || "",
      cheque_number: payment.cheque_number || "",
      notes: payment.notes || "",
    });
  };

  const saveEditPayment = async (paymentId) => {
    try {
      await api.put(`/payments/${paymentId}`, {
        amount: parseFloat(editForm.amount),
        payment_method: editForm.payment_method,
        cheque_bank_name: editForm.payment_method === "cheque" ? editForm.cheque_bank_name : null,
        cheque_number: editForm.payment_method === "cheque" ? editForm.cheque_number : null,
        notes: editForm.notes,
      });
      toast.success("Payment updated successfully");
      setEditingPayment(null);
      setEditForm({ amount: "", payment_method: "cash", cheque_bank_name: "", cheque_number: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const generateReceipt = (payment) => {
    const student = students.find(s => s.user_id === payment.student_id);

    const receiptContent = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          DANCEHUB RECEIPT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Receipt ID: ${payment.payment_id.substring(0, 12)}
Date: ${format(new Date(payment.payment_date), 'dd MMM yyyy, hh:mm a')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
STUDENT DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Name: ${student?.name || 'N/A'}
Phone: ${student?.phone || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PAYMENT DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Amount Paid: ₹${payment.amount}
Payment Method: ${payment.payment_method.toUpperCase()}
${payment.notes ? `Notes: ${payment.notes}` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Thank you for your payment!
Visit us at DanceHub

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt_${payment.payment_id.substring(0, 8)}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success("Receipt downloaded!");
  };

  // ====== REFUND & AUDIT ======
  const startRefund = (payment) => {
    setRefundingPayment(payment);
    setRefundForm({ amount: String(payment.amount_paid || ""), reason: "" });
  };

  const submitRefund = async () => {
    if (!refundForm.reason.trim()) { toast.error("Reason is required"); return; }
    const amt = parseFloat(refundForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/payments/${refundingPayment.payment_id}/refund`, {
        amount: amt,
        reason: refundForm.reason.trim(),
      });
      toast.success("Refund recorded");
      setRefundingPayment(null);
      setRefundForm({ amount: "", reason: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    }
  };

  const openAudit = async (payment) => {
    setAuditPayment(payment);
    setAuditLogs([]);
    try {
      const res = await api.get(`/payments/${payment.payment_id}/audit`);
      setAuditLogs(res.data?.logs || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    }
  };

  // Backend now applies status / type / search filters server-side and returns
  // a single page (default 50). We just render whatever it returned.
  const filteredPayments = payments;

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="payment-management-title">
              Payments
            </h1>
            <p className="text-sm text-slate-600 mt-1">Track payments and generate receipts</p>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) fetchLookups(); }}>
            <DialogTrigger asChild>
              <Button data-testid="record-payment-button" className="h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:opacity-90 font-medium">
                <Plus className="w-5 h-5 mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Record Payment</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="payment-student">Select Student</Label>
                  <select
                    id="payment-student"
                    data-testid="payment-student-select"
                    value={paymentForm.student_id}
                    onChange={(e) => setPaymentForm({ ...paymentForm, student_id: e.target.value, enrollment_id: "" })}
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                  >
                    <option value="">Choose Student</option>
                    {students.map((student) => (
                      <option key={student.user_id} value={student.user_id}>
                        {student.name} - {student.phone}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-enrollment">Select Enrollment</Label>
                  <select
                    id="payment-enrollment"
                    data-testid="payment-enrollment-select"
                    value={paymentForm.enrollment_id}
                    onChange={(e) => setPaymentForm({ ...paymentForm, enrollment_id: e.target.value })}
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                    disabled={!paymentForm.student_id}
                  >
                    <option value="">Choose Enrollment</option>
                    {studentEnrollments.map((enrollment) => {
                      const batch = batches.find(b => b.batch_id === enrollment.batch_id);
                      return (
                        <option key={enrollment.enrollment_id} value={enrollment.enrollment_id}>
                          {batch?.name || enrollment.batch_id.substring(0, 8)} (Active)
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-amount">Amount (₹)</Label>
                  <Input
                    id="payment-amount"
                    data-testid="payment-amount-input"
                    type="number"
                    placeholder="Enter amount"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-method">Payment Method</Label>
                  <select
                    id="payment-method"
                    data-testid="payment-method-select"
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>

                {paymentForm.payment_method === "cheque" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        value={paymentForm.cheque_bank_name}
                        onChange={(e) => setPaymentForm({ ...paymentForm, cheque_bank_name: e.target.value })}
                        placeholder="Enter bank name"
                        className="h-10 rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cheque Number</Label>
                      <Input
                        value={paymentForm.cheque_number}
                        onChange={(e) => setPaymentForm({ ...paymentForm, cheque_number: e.target.value })}
                        placeholder="Enter cheque number"
                        className="h-10 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="payment-notes">Notes (Optional)</Label>
                  <Textarea
                    id="payment-notes"
                    data-testid="payment-notes-input"
                    placeholder="Additional notes..."
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    className="rounded-xl"
                    rows={3}
                  />
                </div>

                <Button
                  data-testid="submit-payment-button"
                  onClick={recordNewPayment}
                  className="w-full h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:opacity-90 font-medium"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Record Payment
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {fetchFailures.length > 0 && (
          <ResilientFetchBanner
            failedLabels={fetchFailures.map((f) => f.label)}
            onRetry={retryFailed}
            retrying={retrying}
            className="mb-3"
            testid="payments-fetch-error"
          />
        )}

        {/* Search bar */}
        <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-slate-100 mb-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              data-testid="payments-search-input"
              placeholder="Search by student or customer name / phone…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-10 sm:h-12 rounded-xl text-sm"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide pb-1">
          <button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              filter === "all" ? "bg-primary text-white" : "bg-white text-slate-600 border hover:bg-slate-50"
            }`}
          >
            All
          </button>
          <button
            data-testid="filter-pending"
            onClick={() => setFilter("pending")}
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
              filter === "pending" ? "bg-orange-500 text-white" : "bg-white text-slate-600 border hover:bg-slate-50"
            }`}
          >
            Pending
          </button>
          <button
            data-testid="filter-completed"
            onClick={() => setFilter("completed")}
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              filter === "completed" ? "bg-green-500 text-white" : "bg-white text-slate-600 border hover:bg-slate-50"
            }`}
          >
            Completed
          </button>
        </div>

        {/* Type Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide pb-1">
          {[
            { key: "all", label: "All Types" },
            { key: "enrollment", label: "Enrollment" },
            { key: "rent", label: "Rent" },
          ].map(t => (
            <button
              key={t.key}
              data-testid={`type-filter-${t.key}`}
              onClick={() => setTypeFilter(t.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                typeFilter === t.key
                  ? "bg-slate-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  typeFilter === t.key ? "bg-white/20 text-white" : "bg-slate-200 text-slate-500"
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">
              {filter === "pending" ? "No pending payments" : filter === "completed" ? "No completed payments" : "No payments recorded yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPayments.map((payment, index) => {
              const isRent = payment.payment_type === "rent";
              const student = !isRent ? (payment.student || students.find(s => s.user_id === payment.student_id)) : null;
              const batch = !isRent ? (payment.batch || batches.find(b => b.batch_id === payment.enrollment?.batch_id)) : null;
              const totalAmount = payment.total_amount || payment.amount || 0;
              const amountPaid = payment.amount_paid || (payment.payment_status === "completed" || payment.payment_status === "paid" ? totalAmount : 0);
              const balanceAmount = payment.balance_amount ?? (totalAmount - amountPaid);
              const isPartial = payment.payment_status === "partial" || (balanceAmount > 0 && amountPaid > 0);
              const isPending = payment.payment_status === "pending" || (amountPaid === 0 && balanceAmount > 0);
              const isPaid = payment.payment_status === "completed" || payment.payment_status === "paid";
              
              return (
                <motion.div
                  key={payment.payment_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index, 20) * 0.02 }}
                  data-testid={`payment-card-${index}`}
                  className={`rounded-2xl p-4 shadow-sm border transition-all ${
                    isPending 
                      ? "bg-orange-50 border-orange-200 hover:border-orange-300"
                      : isPartial
                        ? "bg-amber-50 border-amber-200 hover:border-amber-300"
                        : "bg-white border-slate-100 hover:shadow-lg"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900 truncate">
                          {isRent ? payment.customer_name : (student?.name || 'Unknown')}
                        </p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                          isPending 
                            ? "bg-orange-100 text-orange-700" 
                            : isPartial
                              ? "bg-amber-100 text-amber-700"
                              : "bg-green-100 text-green-700"
                        }`}>
                          {isPending ? "PENDING" : isPartial ? "PARTIAL" : "PAID"}
                        </span>
                        {isRent && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 flex-shrink-0">
                            RENT
                          </span>
                        )}
                      </div>
                      
                      {/* Rent-specific info */}
                      {isRent ? (
                        <>
                          {payment.studio_name && (
                            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {payment.studio_name}{payment.room_name ? ` - ${payment.room_name}` : ''}
                            </p>
                          )}
                          {payment.booking_date && (
                            <p className="text-xs text-slate-500">
                              {format(new Date(payment.booking_date), 'dd MMM yyyy')}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-slate-500 mt-0.5">{student?.phone}</p>
                          {batch && <p className="text-xs text-slate-500">{batch.name}</p>}
                        </>
                      )}
                      
                      {isPartial && (
                        <p className="text-xs text-amber-600 mt-1">
                          Paid ₹{amountPaid} / Balance ₹{balanceAmount}
                        </p>
                      )}
                      {!isPending && !isRent && payment.payment_date && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {payment.payment_method && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                              {payment.payment_method.toUpperCase()}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            {format(new Date(payment.payment_date), 'dd MMM yy')}
                          </span>
                        </div>
                      )}
                      {payment.created_by_name && (
                        <span
                          data-testid={`payment-created-by-${index}`}
                          className={`block text-[11px] mt-1 ${payment.created_by_status === "legacy" ? "text-slate-400 italic" : payment.created_by_status === "inactive" ? "text-amber-600" : "text-slate-500"}`}
                          title={payment.created_at ? `Recorded on ${new Date(payment.created_at).toLocaleString()}` : ""}
                        >
                          Recorded by {payment.created_by_name}
                        </span>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-xl sm:text-2xl font-bold font-heading ${
                        isPending ? "text-orange-600" : isPartial ? "text-amber-600" : "text-green-600"
                      }`}>
                        ₹{totalAmount}
                      </p>
                      {(isPending || isPartial) ? (
                        <Button
                          data-testid={`record-pending-${index}`}
                          onClick={() => {
                            if (isRent) {
                              setRecordingRentPayment(payment.payment_id);
                              setRentPayForm({ amount: "", payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'), reference_number: "", notes: "" });
                            } else {
                              setRecordingPayment(payment.payment_id);
                            }
                          }}
                          size="sm"
                          className="mt-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-xs h-8 px-3"
                        >
                          {isPartial ? "Pay Balance" : "Record"}
                        </Button>
                      ) : (
                        <div className="flex gap-1 mt-2">
                          {!isRent && user?.role !== "instructor" && (
                            <Button
                              data-testid={`edit-payment-${index}`}
                              onClick={() => startEditPayment(payment)}
                              size="sm"
                              className="rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 w-8 h-8 p-0"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {!isRent && (
                            <Button
                              data-testid={`download-receipt-${index}`}
                              onClick={() => generateReceipt(payment)}
                              size="sm"
                              className="rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 w-8 h-8 p-0"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isAdmin(user) && (payment.amount_paid || 0) > 0 && payment.payment_status !== "refunded" && (
                            <Button
                              data-testid={`refund-payment-${index}`}
                              onClick={() => startRefund(payment)}
                              size="sm"
                              title="Refund / Reverse"
                              className="rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 w-8 h-8 p-0"
                            >
                              <Undo2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isAdmin(user) && (
                            <Button
                              data-testid={`audit-payment-${index}`}
                              onClick={() => openAudit(payment)}
                              size="sm"
                              title="View audit trail"
                              className="rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 w-8 h-8 p-0"
                            >
                              <History className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edited indicator */}
                  {payment.is_edited && payment.last_edited_at && (
                    <div className="mt-3 flex items-center gap-1 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>Edited on {format(new Date(payment.last_edited_at), 'dd MMM yyyy, hh:mm a')}</span>
                    </div>
                  )}

                  {/* Rent Payment Recording Form */}
                  {isRent && recordingRentPayment === payment.payment_id && (
                    <div className="mt-4 pt-4 border-t border-orange-200 space-y-3">
                      <div className="space-y-2">
                        <Label className="text-sm">Amount to Pay (₹)</Label>
                        <Input
                          data-testid="rent-pay-amount"
                          type="number"
                          value={rentPayForm.amount}
                          onChange={(e) => setRentPayForm({...rentPayForm, amount: e.target.value})}
                          placeholder={`Max: ₹${balanceAmount}`}
                          className="h-10 rounded-lg"
                        />
                        <p className="text-xs text-slate-500">Outstanding: ₹{balanceAmount}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Method</Label>
                          <select
                            value={rentPayForm.payment_method}
                            onChange={(e) => setRentPayForm({...rentPayForm, payment_method: e.target.value})}
                            className="w-full h-10 rounded-lg border px-3 bg-white text-sm"
                          >
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Date</Label>
                          <Input type="date" value={rentPayForm.payment_date} onChange={(e) => setRentPayForm({...rentPayForm, payment_date: e.target.value})} className="h-10 rounded-lg text-sm" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Reference #</Label>
                          <Input value={rentPayForm.reference_number} onChange={(e) => setRentPayForm({...rentPayForm, reference_number: e.target.value})} placeholder="Optional" className="h-10 rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Notes</Label>
                          <Input value={rentPayForm.notes} onChange={(e) => setRentPayForm({...rentPayForm, notes: e.target.value})} placeholder="Optional" className="h-10 rounded-lg text-sm" />
                        </div>
                      </div>

                      {/* Rent payment history */}
                      {payment.payment_history && payment.payment_history.length > 0 && (
                        <div className="space-y-1 p-2 bg-slate-50 rounded-lg">
                          <p className="text-xs font-medium text-slate-600">Payment History:</p>
                          {payment.payment_history.map((txn, idx) => (
                            <div key={`${txn.date || ""}-${txn.amount}-${txn.method || txn.payment_method || ""}-${idx}`} className="flex items-center justify-between text-xs text-slate-600">
                              <span>₹{txn.amount} via {(txn.method || txn.payment_method || "").replace("_", " ")}</span>
                              <span>{txn.date ? format(new Date(txn.date), "dd MMM yyyy") : "N/A"}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setRecordingRentPayment(null)} className="flex-1 rounded-full">Cancel</Button>
                        <Button size="sm" onClick={() => recordRentPayment(payment.payment_id, payment.booking_id)} className="flex-1 rounded-full bg-green-500 hover:bg-green-600">
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Record ₹{rentPayForm.amount || balanceAmount}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Record Payment Form (Enrollment) */}
                  {!isRent && recordingPayment === payment.payment_id && (
                    <div className="mt-4 pt-4 border-t border-orange-200 space-y-3">
                      <div className="space-y-2">
                        <Label className="text-sm">Amount to Pay (₹)</Label>
                        <Input
                          data-testid="record-payment-amount-main"
                          type="number"
                          value={recordForm.record_amount}
                          onChange={(e) => setRecordForm({...recordForm, record_amount: e.target.value})}
                          placeholder={`Max: ₹${balanceAmount}`}
                          max={balanceAmount}
                          className="h-10 rounded-lg"
                        />
                        <p className="text-xs text-slate-500">Outstanding: ₹{balanceAmount}. Pay any amount.</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Payment Method</Label>
                        <select
                          value={recordForm.payment_method}
                          onChange={(e) => setRecordForm({...recordForm, payment_method: e.target.value})}
                          className="w-full h-10 rounded-lg border px-3 bg-white"
                        >
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="cheque">Cheque</option>
                        </select>
                      </div>

                      {recordForm.payment_method === "cheque" && (
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={recordForm.cheque_bank_name}
                            onChange={(e) => setRecordForm({...recordForm, cheque_bank_name: e.target.value})}
                            placeholder="Bank name"
                            className="h-10 rounded-lg"
                          />
                          <Input
                            value={recordForm.cheque_number}
                            onChange={(e) => setRecordForm({...recordForm, cheque_number: e.target.value})}
                            placeholder="Cheque number"
                            className="h-10 rounded-lg"
                          />
                        </div>
                      )}

                      {/* Payment History */}
                      {payment.payment_history && payment.payment_history.length > 0 && (
                        <div className="space-y-1 p-2 bg-slate-50 rounded-lg">
                          <p className="text-xs font-medium text-slate-600">Payment History:</p>
                          {payment.payment_history.map((txn, idx) => (
                            <div key={`${txn.date || ""}-${txn.amount}-${txn.method || ""}-${idx}`} className="flex items-center justify-between text-xs text-slate-600">
                              <span>₹{txn.amount} via {txn.method}</span>
                              <span>{format(new Date(txn.date), "dd MMM yyyy")}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRecordingPayment(null)}
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => recordPendingPayment(payment.payment_id)}
                          className="flex-1 rounded-full bg-green-500 hover:bg-green-600"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Record ₹{recordForm.record_amount || balanceAmount}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Edit Payment Form (Enrollment only) */}
                  {!isRent && editingPayment === payment.payment_id && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                      <h4 className="font-medium text-slate-900">Edit Payment</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Amount (₹)</Label>
                          <Input
                            type="number"
                            value={editForm.amount}
                            onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                            className="h-10 rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Payment Method</Label>
                          <select
                            value={editForm.payment_method}
                            onChange={(e) => setEditForm({...editForm, payment_method: e.target.value})}
                            className="w-full h-10 rounded-lg border px-3 bg-white"
                          >
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>
                      </div>

                      {editForm.payment_method === "cheque" && (
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={editForm.cheque_bank_name}
                            onChange={(e) => setEditForm({...editForm, cheque_bank_name: e.target.value})}
                            placeholder="Bank name"
                            className="h-10 rounded-lg"
                          />
                          <Input
                            value={editForm.cheque_number}
                            onChange={(e) => setEditForm({...editForm, cheque_number: e.target.value})}
                            placeholder="Cheque number"
                            className="h-10 rounded-lg"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-sm">Notes</Label>
                        <Input
                          value={editForm.notes}
                          onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                          placeholder="Additional notes"
                          className="h-10 rounded-lg"
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingPayment(null)}
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveEditPayment(payment.payment_id)}
                          className="flex-1 rounded-full bg-primary"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}

                  {payment.notes && !editingPayment && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-600">{payment.notes}</p>
                    </div>
                  )}
                </motion.div>
              );
            })}
            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  data-testid="load-more-payments-btn"
                  variant="outline"
                  onClick={loadMorePayments}
                  disabled={loadingMore}
                  className="rounded-full"
                >
                  {loadingMore ? "Loading..." : `Load more (${totalPayments - payments.length} remaining)`}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />

      {/* Refund Dialog */}
      <Dialog open={!!refundingPayment} onOpenChange={(o) => { if (!o) setRefundingPayment(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Refund / Reverse Payment</DialogTitle>
          </DialogHeader>
          {refundingPayment && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3">
                <div>Paid so far: <strong className="text-slate-900">₹{refundingPayment.amount_paid || 0}</strong></div>
                {refundingPayment.refunded_amount ? (
                  <div className="text-amber-700">Already refunded: ₹{refundingPayment.refunded_amount}</div>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Refund Amount (₹)</Label>
                <Input
                  data-testid="refund-amount-input"
                  type="number"
                  step="0.01"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                />
                <p className="text-xs text-slate-500">Cannot exceed amount paid (₹{refundingPayment.amount_paid || 0}).</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Reason</Label>
                <Textarea
                  data-testid="refund-reason-input"
                  rows={3}
                  placeholder="e.g., Customer dropped out, billing correction..."
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRefundingPayment(null)}>Cancel</Button>
                <Button
                  data-testid="refund-submit-button"
                  onClick={submitRefund}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Undo2 className="w-4 h-4 mr-1" /> Confirm Refund
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={!!auditPayment} onOpenChange={(o) => { if (!o) { setAuditPayment(null); setAuditLogs([]); } }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Trail</DialogTitle>
          </DialogHeader>
          {auditLogs.length === 0 && (
            <p className="text-sm text-slate-500 py-4">No audit entries yet.</p>
          )}
          <div className="space-y-2">
            {auditLogs.map((log) => (
              <div key={log.audit_id} data-testid={`audit-row-${log.audit_id}`} className="border border-slate-200 rounded-xl p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900 capitalize">{log.action?.replace("_", " ")}</span>
                  <span className="text-xs text-slate-400">{format(new Date(log.timestamp), "dd MMM yyyy, hh:mm a")}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  by <span className="font-medium text-slate-700">{log.actor_name}</span>
                  <span className="ml-1 capitalize">({log.actor_role?.replace("_", " ")})</span>
                </div>
                {log.extra && Object.keys(log.extra).length > 0 && (
                  <div className="text-xs text-slate-600 mt-2 bg-slate-50 rounded p-2">
                    {log.extra.amount !== undefined && <div>Amount: ₹{log.extra.amount}</div>}
                    {log.extra.reason && <div>Reason: {log.extra.reason}</div>}
                    {log.extra.transaction && (
                      <div>
                        Recorded: ₹{log.extra.transaction.amount} via {log.extra.transaction.method}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentManagement;
